import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { Errors } from '../helpers/Errors';
import {
  getTicketMaxByUserExceededMessageParams,
  ticketI18nKey,
} from '../helpers/ticket-translate.helper';
import { showWaitingListMaxExceededSwal } from '../pages/ticketing/utils/waiting-list-error.swal';
import { CommunityService } from './community/community.service';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorDisplayService {

  constructor(
    private translate: TranslateService,
    private communityService: CommunityService,
  ) {}

  private currentCommunityCode(): string | null {
    let code: string | null = null;
    const sub = this.communityService.Get().subscribe(c => {
      code = c?.code ?? null;
    });
    sub.unsubscribe();
    return code;
  }

  showErrorToUser(err: HttpErrorResponse, suppressGeneric = false): void {
    if (err?.status === 0) {
      Swal.fire({
        title: this.translate.instant('ERRORS.Connection.Title'),
        html: this.translate.instant('ERRORS.Connection.Text'),
        icon: 'error',
        confirmButtonText: this.translate.instant('Accept'),
        customClass: {
          title: 'long-title'
        }
      });
      return;
    }

    const body = this.getErrorBody(err);
    const code = (body?.code ?? body?.Code) as string | undefined;

    if (code === 'EMAIL_ALREADY_EXISTS') {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('WAITINGLIST.EmailExistsTitle'),
        text: this.translate.instant('WAITINGLIST.EmailExistsText'),
        confirmButtonText: this.translate.instant('WAITINGLIST.accordButton'),
        customClass: { confirmButton: 'swal-confirm-corp' },
      });
      return;
    }

    if (code === 'WAITING_LIST_MAX_EXCEEDED') {
      const purchasedCount = Number(body?.purchasedCount ?? body?.PurchasedCount ?? 0);
      const alreadyRequested = Number(body?.alreadyRequested ?? body?.AlreadyRequested ?? 0);
      const maxAllowed = Number(body?.maxAllowed ?? body?.MaxAllowed ?? 0);
      const maxLimit = Number(body?.maxLimit ?? body?.MaxLimit ?? 0);
      const max = maxLimit > 0 ? maxLimit : purchasedCount + alreadyRequested + maxAllowed;
      void showWaitingListMaxExceededSwal(this.translate, max);
      return;
    }

    if (code === 'NoMorePlacesAvailable') {
      Swal.fire({
        title: this.translate.instant('Oops'),
        html: this.translate.instant('ReservePlaces.NoMorePlacesAvailable'),
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    if (code === 'TicketMaxByFieldExceeded') {
      Swal.fire({
        title: this.translate.instant('TICKETS.MaxByFieldExceededTitle'),
        text: this.translate.instant('TICKETS.MaxByFieldExceededMessage'),
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    if (code === 'TicketMaxByUserExceeded' || code === 'TicketMaxByUserInvalidLimitField') {
      const maxByUser = body?.maxByUser ?? body?.value ?? 1;
      const communityCode = this.currentCommunityCode();
      Swal.fire({
        title: this.translate.instant(ticketI18nKey(communityCode, 'MaxByUserExceededTitle')),
        text: this.translate.instant(
          'TICKETS.MaxByUserExceededMessage',
          getTicketMaxByUserExceededMessageParams(this.translate, maxByUser, communityCode),
        ),
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    if (code === 'TicketMaxByUserLimitFieldRequired') {
      Swal.fire({
        title: this.translate.instant('Oops'),
        text: this.translate.instant('TICKETS.MaxByUserLimitFieldRequired'),
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    if (code === 'InscriptionMaxMultipleExceeded') {
      const maxValue = body?.value ?? body?.maxByUser ?? 1;
      Swal.fire({
        title: this.translate.instant('Oops'),
        text: this.translate.instant('INSCRIPTIONS.MaxMultipleExceeded', { value: maxValue }),
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    const userMessage = body?.UserMessage ?? body?.userMessage;
    if (userMessage) {
      Swal.fire({
        title: this.translate.instant('Oops'),
        html: userMessage,
        icon: 'warning',
        confirmButtonText: this.translate.instant('Accept'),
      });
      return;
    }

    if (!suppressGeneric) {
      Errors.showGenericError(this.translate);
    }
  }

  private getErrorBody(err: HttpErrorResponse): any {
    const body = err?.error;
    if (body == null) {
      return null;
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return { message: body };
      }
    }
    return body;
  }
}
